import { Injectable } from '@angular/core';
import {
  PAYMENTS_TWOCHECKOUT_INITIATE_PATH,
  EXTERNAL_SESSION_AUTH_SCHEME,
  SUBSCRIPTION_EXTERNAL_PROVIDER_REGISTER_FIRST_ACTIVITY_ENDPOINT,
} from '@core/constant/endpoints';

import { HTTPService } from './http.service';
import {
  SubscriptionCreateBody,
  SubscriptionCreateWithPaymentBody,
  SubscriptionCreateWithPaymentResponseDto,
  SubscriptionDetailsDto,
  SubscriptionUpdateDto,
  GetSubscriptionsWithPaginationParam,
  ExternalProviderSubscriptionInitResultDto,
  UpdateAutoRenewBody,
  UpdateAutoRenewResponseDto,
  TwoCheckoutInitiateBody,
  TwoCheckoutInitiateResultDto,
  TwoCheckoutPaymentStatusDto,
  CreatePendingForQrBody,
  PendingSubscriptionWithQrResultDto,
  ConfirmQrPaymentRequestDto,
  CheckQrPaymentStatusRequestDto,
  ConfirmEpostaRedirectPaymentRequestDto,
  SubscriptionPaymentResultDto,
} from '@core/models/api/subscription.model';
import { BaseDataPagination } from '@core/models/utilities.models';

@Injectable({
  providedIn: 'root',
})
export class SubscriptionService {
  constructor(private httpService: HTTPService) {}

  createSubscription = (body: SubscriptionCreateBody) => {
    return this.httpService.create<any>({
      resource: '/Subscription/Create',
      body,
    });
  };

  /** Create subscription with payment; SessionId is sent in Authorization header (backend uses it from there). */
  createSubscriptionWithPayment = (body: SubscriptionCreateWithPaymentBody) => {
    const { SessionId, ...rest } = body;
    const bodyWithoutToken = { ...rest, autoRenew: body.autoRenew ?? true };
    return this.httpService.create<SubscriptionCreateWithPaymentResponseDto>({
      resource: '/Subscription/CreateWithPayment',
      headers: { Authorization: `${EXTERNAL_SESSION_AUTH_SCHEME} ${SessionId}` },
      body: bodyWithoutToken,
    });
  };

  /**Create pending subscription and get 6-digit code */
  createPendingForQr = (body: CreatePendingForQrBody, sessionId: string) => {
    return this.httpService.create<PendingSubscriptionWithQrResultDto>({
      resource: '/Subscription/CreatePendingForQr',
      headers: { Authorization: `${EXTERNAL_SESSION_AUTH_SCHEME} ${sessionId}` },
      body,
    });
  };

  /**Confirm QR payment: create payment in Pending. */
  confirmQrPayment = (body: ConfirmQrPaymentRequestDto, sessionId: string) => {
    return this.httpService.create<SubscriptionPaymentResultDto>({
      resource: '/Subscription/ConfirmQrPayment',
      headers: { Authorization: `${EXTERNAL_SESSION_AUTH_SCHEME} ${sessionId}` },
      body,
    });
  };

  /** Check QR payment status: verifies wallet for transfer with Description = QrPaymentCode; returns success/failed. */
  checkQrPaymentStatus = (body: CheckQrPaymentStatusRequestDto, sessionId: string) => {
    return this.httpService.create<SubscriptionPaymentResultDto>({
      resource: '/Subscription/CheckQrPaymentStatus',
      headers: { Authorization: `${EXTERNAL_SESSION_AUTH_SCHEME} ${sessionId}` },
      body,
    });
  };

  /** Confirm E-Posta redirect payment after external system redirects back with success code. */
  confirmEpostaRedirectPayment = (successCode: string, sessionId: string) => {
    return this.httpService.create<SubscriptionPaymentResultDto>({
      resource: '/Subscription/ConfirmEpostaRedirectPayment',
      headers: { Authorization: `${EXTERNAL_SESSION_AUTH_SCHEME} ${sessionId}` },
      body: { successCode },
    });
  };

  /** Mark E-Posta redirect payment as failed after external system redirects to fail URL. */
  failEpostaRedirectPayment = (successCode: string, sessionId: string) => {
    return this.httpService.create<SubscriptionPaymentResultDto>({
      resource: '/Subscription/FailEpostaRedirectPayment',
      headers: { Authorization: `${EXTERNAL_SESSION_AUTH_SCHEME} ${sessionId}` },
      body: { successCode },
    });
  };

  /** Initialize external provider session; POST Auth/ExternalProvider/Initialize with SessionId in Authorization header */
  initializeExternalProvider = (SessionId: string) => {
    return this.httpService.create<ExternalProviderSubscriptionInitResultDto>({
      resource: '/Auth/ExternalProvider/Initialize',
      headers: { Authorization: `${EXTERNAL_SESSION_AUTH_SCHEME} ${SessionId}` },
      body: {},
    });
  };

  /** Update auto-renew for current subscription; PATCH Subscription/UpdateAutoRenew (external session). */
  updateAutoRenew = (SessionId: string, body: UpdateAutoRenewBody) => {
    return this.httpService.patch<UpdateAutoRenewResponseDto>({
      resource: '/Subscription/UpdateAutoRenew',
      headers: { Authorization: `${EXTERNAL_SESSION_AUTH_SCHEME} ${SessionId}` },
      body,
      });
  };

  /** 2Checkout hosted checkout: creates pending subscription and returns checkout URL. */
  initiateTwoCheckout = (body: TwoCheckoutInitiateBody, sessionId: string) => {
    return this.httpService.create<TwoCheckoutInitiateResultDto>({
      resource: PAYMENTS_TWOCHECKOUT_INITIATE_PATH,
      headers: { Authorization: `${EXTERNAL_SESSION_AUTH_SCHEME} ${sessionId}` },
      body: { subsPlanId: body.subsPlanId, price: body.price, sessionId },
    });
  };

  /** Poll payment status after return from 2Checkout (same ExternalSession as initiate). */
  getTwoCheckoutPaymentStatus = (merchantOrderId: string, sessionId: string) => {
    return this.httpService.get<TwoCheckoutPaymentStatusDto>({
      resource: `/payments/2checkout/status/${encodeURIComponent(merchantOrderId)}`,
      headers: { Authorization: `${EXTERNAL_SESSION_AUTH_SCHEME} ${sessionId}` },
      query: {},
    });
  };

  registerExternalProviderFirstActivity = (SessionId: string) => {
    return this.httpService.create<void>({
      resource: SUBSCRIPTION_EXTERNAL_PROVIDER_REGISTER_FIRST_ACTIVITY_ENDPOINT,
      headers: { Authorization: `${EXTERNAL_SESSION_AUTH_SCHEME} ${SessionId}` },
      body: {},
    });
  };

  // Get all subscription details using the view
  getAllSubscriptionDetails = (businessClientId?: number | null) => { 
    const params: any = {};
    if (businessClientId != null) {
      params.businessClientId = businessClientId;
    }
    return this.httpService.gets<SubscriptionDetailsDto>({
      resource: '/Subscription',
      query: params,
    });
  };

  // Get subscription details by ID using the view
  getSubscriptionDetailsById = (id: number) => {
    return this.httpService.get<SubscriptionDetailsDto>({
      resource: `/Subscription/${id}`,
    });
  };

  // Get subscriptions with pagination using the view
  getSubscriptionsWithPagination = (param: GetSubscriptionsWithPaginationParam) => {
    return this.httpService.getPagination<SubscriptionDetailsDto>({
      resource: '/Subscription/GetSubscriptionsWithPagination',
      query: param,
    });
  };

  // Update subscription (includes optional usage updates)
  updateSubscription = (id: number, body: SubscriptionUpdateDto) => {
    return this.httpService.update<SubscriptionDetailsDto>({
      resource: `/Subscription/${id}`,
      body,
    });
  };
}
